import { zodResolver } from "@hookform/resolvers/zod";
import type { ImpersonationMerchantRole, ScenarioConfigInput, ScenarioRun, ScenarioSeedOption } from "@unbox/api";
import { ArrowLeft, Check, Copy01, Plus, Trash01, XClose } from "@untitledui/icons";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import z from "zod/v3";
import {
  useCreateScenario,
  useDeleteScenarioConfig,
  useSavedScenarioConfigs,
  useSaveScenarioConfig,
  useScenarioOptions,
} from "@/api/admin/scenarios";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { NativeSelect } from "@/components/base/select/select-native";
import { Toggle } from "@/components/base/toggle/toggle";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cx } from "@/utils/cx";
import { ROLE_DESCRIPTIONS } from "./constants";
import { roleLabel, slugEmail } from "./model";

const schema = z.object({
  name: z.string().trim().min(1, "Give the scenario a name"),
  password: z.string().refine((value) => value === "" || value.length >= 8, "Use at least 8 characters"),
  workspaces: z
    .array(
      z.object({
        name: z.string().trim().min(1, "Name the workspace"),
        role: z.enum(["workspace_admin", "member"]),
        merchants: z
          .array(z.object({ template: z.string(), role: z.enum(["merchant_admin", "user"]) }))
          .min(1, "Pick at least one brand"),
      }),
    )
    .min(1, "Add at least one workspace"),
  scenarios: z.array(z.string()),
});

type FormValues = z.infer<typeof schema>;
type FormWorkspace = FormValues["workspaces"][number];
type ScenarioMerchantRole = FormWorkspace["merchants"][number]["role"];

const MERCHANT_ROLE_OPTIONS = (["merchant_admin", "user"] as const).map((role) => ({
  value: role,
  label: roleLabel(role),
}));

/**
 * The workspace's role as one choice: workspace admin, or the role every brand
 * in it shares. Brands with different roles read as mixed.
 */
const workspaceChoice = (workspace: Pick<FormWorkspace, "role" | "merchants">): ImpersonationMerchantRole | "mixed" => {
  if (workspace.role === "workspace_admin") return "workspace_admin";
  const roles = [...new Set(workspace.merchants.map((merchant) => merchant.role))];
  if (roles.length > 1) return "mixed";
  return roles[0] ?? "user";
};

type Props = {
  runs: ScenarioRun[];
  onBack: () => void;
  onClose: () => void;
  /** Shows a scenario's user in the list, for one picked below or one just created. */
  onOpen: (user: { id: string; email: string }) => void;
};

const Section = ({
  step,
  title,
  sub,
  aside,
  children,
}: {
  step: number;
  title: string;
  sub?: string;
  aside?: ReactNode;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5">
    <div className="flex items-start gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-purple-950 text-xs font-semibold text-white">
        {step}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <h3 className="text-md font-semibold text-gray-900">{title}</h3>
        {sub && <p className="text-sm text-gray-600">{sub}</p>}
      </div>
      {aside}
    </div>
    {children}
  </section>
);

const IconButton = ({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-900/5 hover:text-gray-700"
  >
    {children}
  </button>
);

/** Turning a scenario on brings what it needs with it; turning one off takes what needs it away. */
const toggleScenario = (options: ScenarioSeedOption[], selected: string[], name: string, on: boolean): string[] => {
  const next = new Set(selected);
  const visit = (current: string) => {
    if (on ? next.has(current) : !next.has(current)) return;
    if (on) next.add(current);
    else next.delete(current);
    const option = options.find((o) => o.name === current);
    const related = on
      ? (option?.requires ?? [])
      : options.filter((o) => o.requires.includes(current)).map((o) => o.name);
    related.forEach(visit);
  };
  visit(name);
  return options.map((o) => o.name).filter((o) => next.has(o));
};

/** The roles a created scenario's user has, across all its workspaces. */
const runRoleSummary = (run: ScenarioRun): string => {
  const choices = [...new Set(run.config.workspaces.map(workspaceChoice))];
  return choices.length === 1 && choices[0] !== "mixed" ? roleLabel(choices[0]) : "Mixed roles";
};

const toConfig = (values: FormValues): ScenarioConfigInput => ({
  email: slugEmail(values.name),
  workspaces: values.workspaces,
  scenarios: values.scenarios,
});

export const CreateScenario = ({ runs, onBack, onClose, onOpen }: Props) => {
  const { data: options } = useScenarioOptions();
  const { data: configs = [] } = useSavedScenarioConfigs();
  const createScenario = useCreateScenario();
  const saveConfig = useSaveScenarioConfig();
  const deleteConfig = useDeleteScenarioConfig();
  const [configId, setConfigId] = useState("");

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", password: "", workspaces: [], scenarios: [] },
  });
  const workspaces = useFieldArray({ control: form.control, name: "workspaces" });
  const { errors } = form.formState;

  const templates = options?.templates ?? [];
  const seedOptions = options?.scenarios ?? [];

  const randomName = () => {
    const parts = options?.workspaceNameParts;
    if (!parts) return "";
    const pick = (pool: string[]) => pool[Math.floor(Math.random() * pool.length)];
    return `${pick(parts.first)} ${pick(parts.second)}`;
  };
  // A new workspace holds every brand until some are unticked.
  const newWorkspace = (): FormWorkspace => ({
    name: randomName(),
    role: "member",
    merchants: templates.map((template) => ({ template: template.key, role: "user" })),
  });

  useEffect(() => {
    if (options && form.getValues("workspaces").length === 0) workspaces.replace([newWorkspace()]);
  }, [options]);
  const groups = useMemo(() => {
    const byArea = new Map<string, ScenarioSeedOption[]>();
    for (const option of seedOptions) byArea.set(option.areaLabel, [...(byArea.get(option.areaLabel) ?? []), option]);
    return [...byArea];
  }, [seedOptions]);

  // Everything is on until the person filling the form says otherwise.
  useEffect(() => {
    if (seedOptions.length && !form.formState.dirtyFields.scenarios) {
      form.setValue(
        "scenarios",
        seedOptions.map((option) => option.name),
      );
    }
  }, [seedOptions, form]);

  const name = form.watch("name");
  const scenarios = form.watch("scenarios");
  const watchedWorkspaces = form.watch("workspaces");
  const email = slugEmail(name);

  const [copied, setCopied] = useState(false);
  const copyCredentials = async () => {
    const password = form.getValues("password") || options?.defaultPassword || "";
    await navigator.clipboard.writeText(`${name.trim()}\nEmail: ${email}\nPassword: ${password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const brandCount = watchedWorkspaces.reduce((total, workspace) => total + workspace.merchants.length, 0);
  const setScenarios = (next: string[]) => form.setValue("scenarios", next, { shouldDirty: true });

  const setMerchants = (index: number, merchants: FormWorkspace["merchants"]) =>
    form.setValue(`workspaces.${index}.merchants`, merchants, { shouldValidate: form.formState.isSubmitted });

  // A brand added to a workspace takes the role its other brands share.
  const toggleBrand = (index: number, key: string) => {
    const workspace = form.getValues(`workspaces.${index}`);
    if (workspace.merchants.some((merchant) => merchant.template === key)) {
      setMerchants(
        index,
        workspace.merchants.filter((merchant) => merchant.template !== key),
      );
      return;
    }
    const choice = workspaceChoice(workspace);
    const role: ScenarioMerchantRole = choice === "user" || choice === "mixed" ? "user" : "merchant_admin";
    setMerchants(index, [...workspace.merchants, { template: key, role }]);
  };

  /** Sets the role on the workspace and on every brand in it at once. */
  const setWorkspaceRole = (index: number, role: ImpersonationMerchantRole) => {
    const workspace = form.getValues(`workspaces.${index}`);
    form.setValue(`workspaces.${index}.role`, role === "workspace_admin" ? "workspace_admin" : "member");
    setMerchants(
      index,
      workspace.merchants.map((merchant) => ({ ...merchant, role: role === "user" ? "user" : "merchant_admin" })),
    );
  };

  const setMerchantRole = (index: number, key: string, role: ScenarioMerchantRole) =>
    setMerchants(
      index,
      form
        .getValues(`workspaces.${index}.merchants`)
        .map((merchant) => (merchant.template === key ? { ...merchant, role } : merchant)),
    );

  const loadConfig = (id: string) => {
    setConfigId(id);
    const saved = configs.find((c) => c.id === id);
    if (!saved) return;
    form.reset({
      name: saved.name,
      password: "",
      workspaces: saved.config.workspaces.map((workspace) => ({ ...workspace, name: workspace.name ?? randomName() })),
      scenarios: saved.config.scenarios,
    });
  };

  const removeConfig = async () => {
    await deleteConfig.mutateAsync(configId);
    setConfigId("");
  };

  const save = form.handleSubmit(async (values) => {
    const saved = await saveConfig.mutateAsync({ name: values.name.trim(), config: toConfig(values) });
    setConfigId(saved.id);
  });

  const create = form.handleSubmit(async (values) => {
    const run = await createScenario.mutateAsync({
      name: values.name.trim(),
      password: values.password || undefined,
      config: toConfig(values),
    });
    onOpen(run.user);
  });

  const failure = createScenario.error ?? saveConfig.error;

  return (
    <div className="flex max-h-[85vh] min-w-0 flex-col gap-4 bg-unbox-cream-25 p-6">
      <div className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-3">
          <IconButton label="Back to users" onClick={onBack}>
            <ArrowLeft className="size-[18px]" />
          </IconButton>
          <div className="flex flex-col gap-1">
            <DialogTitle className="text-lg font-semibold text-gray-900">Create a scenario</DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Seed fresh workspaces from the brand templates, with exactly the access and data you want to test with.
            </DialogDescription>
          </div>
        </div>
        <IconButton label="Close" onClick={onClose}>
          <XClose className="size-5" />
        </IconButton>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-3">
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-semibold text-gray-900">Load a saved scenario</span>
            <span className="text-xs text-gray-500">
              {configs.length
                ? "Loads the name and every choice below. Change anything you like before creating or saving again."
                : "Save a scenario and it will appear here."}
            </span>
          </div>
          <NativeSelect
            aria-label="Saved scenario"
            size="sm"
            className="ml-auto w-auto min-w-[240px]"
            value={configId}
            disabled={!configs.length}
            onChange={(event) => loadConfig(event.target.value)}
            options={[
              { value: "", label: configs.length ? "Choose a scenario…" : "No saved scenarios yet" },
              ...configs.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          {configId && (
            <Button color="link-gray" size="sm" onClick={removeConfig} isDisabled={deleteConfig.isPending}>
              Delete
            </Button>
          )}
        </div>

        <Section step={1} title="Name this scenario">
          <div className="grid w-full max-w-[640px] grid-cols-2 gap-3">
            <Input
              label="Name"
              // biome-ignore lint/a11y/noAutofocus: the name is the first thing to fill in on this view
              autoFocus
              value={name}
              onChange={(value) => form.setValue("name", value, { shouldValidate: form.formState.isSubmitted })}
              placeholder="e.g. Safety reviewer, one merchant"
              isInvalid={!!errors.name}
              hint={errors.name?.message}
            />
            <Input
              label="Password"
              type="password"
              value={form.watch("password")}
              onChange={(value) => form.setValue("password", value, { shouldValidate: form.formState.isSubmitted })}
              placeholder="Leave blank for the seed password"
              isInvalid={!!errors.password}
              hint={errors.password?.message}
            />
          </div>
          {email && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-gray-500">
                The scenario signs in as <span className="font-mono text-gray-700">{email}</span>
              </p>
              <Button color="secondary" size="sm" iconLeading={copied ? Check : Copy01} onClick={copyCredentials}>
                {copied ? "Copied" : "Copy name and sign-in"}
              </Button>
            </div>
          )}
        </Section>

        <Section
          step={2}
          title="Which workspaces and brands are in this scenario?"
          sub="Add as many workspaces as you like. Each gets one merchant per brand ticked in it, seeded fresh. Set a role on each brand, or on the workspace to set them all at once."
          aside={
            <Button color="secondary" size="sm" iconLeading={Plus} onClick={() => workspaces.append(newWorkspace())}>
              Add workspace
            </Button>
          }
        >
          {errors.workspaces?.root?.message && (
            <p className="text-sm text-error-primary">{errors.workspaces.root.message}</p>
          )}
          <div className="flex flex-col gap-3">
            {workspaces.fields.map((field, index) => {
              const workspace = watchedWorkspaces[index] ?? { name: "", role: "member", merchants: [] };
              const choice = workspaceChoice(workspace);
              const isAdmin = workspace.role === "workspace_admin";
              const error = errors.workspaces?.[index]?.merchants?.message;
              return (
                <div key={field.id} className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3">
                  <div className="flex items-end gap-3 px-1">
                    <div className="flex w-[240px] flex-col gap-1">
                      <span className="text-xs font-medium text-gray-500">Workspace name</span>
                      <Input
                        aria-label={`Name of workspace ${index + 1}`}
                        size="sm"
                        value={workspace.name}
                        onChange={(value) =>
                          form.setValue(`workspaces.${index}.name`, value, {
                            shouldValidate: form.formState.isSubmitted,
                          })
                        }
                        isInvalid={!!errors.workspaces?.[index]?.name}
                        hint={errors.workspaces?.[index]?.name?.message}
                      />
                    </div>
                    <span
                      className={cx("ml-auto text-xs tabular-nums", error ? "text-error-primary" : "text-gray-400")}
                    >
                      {error ?? `${workspace.merchants.length} of ${templates.length} brands`}
                    </span>
                    <NativeSelect
                      aria-label={`Role in workspace ${index + 1}`}
                      size="sm"
                      className="w-auto"
                      value={choice}
                      onChange={(event) => setWorkspaceRole(index, event.target.value as ImpersonationMerchantRole)}
                      options={[
                        ...ROLE_DESCRIPTIONS.map(([role]) => ({ value: role, label: roleLabel(role) })),
                        ...(choice === "mixed" ? [{ value: "mixed", label: "Mixed roles", disabled: true }] : []),
                      ]}
                    />
                    {workspaces.fields.length > 1 && (
                      <IconButton label={`Remove workspace ${index + 1}`} onClick={() => workspaces.remove(index)}>
                        <Trash01 className="size-4" />
                      </IconButton>
                    )}
                  </div>
                  {choice !== "mixed" && (
                    <p className="px-1 text-xs text-gray-500">
                      {ROLE_DESCRIPTIONS.find(([role]) => role === choice)?.[1]}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-1.5">
                    {templates.map((template) => {
                      const merchant = workspace.merchants.find((m) => m.template === template.key);
                      return (
                        <div
                          key={template.key}
                          className={cx(
                            "flex items-center gap-2.5 rounded-md border px-3 py-2 transition-colors",
                            merchant ? "border-purple-950 bg-purple-50" : "border-gray-200 hover:bg-gray-50",
                          )}
                        >
                          <Checkbox
                            className="min-w-0 flex-1 items-center"
                            isSelected={!!merchant}
                            onChange={() => toggleBrand(index, template.key)}
                            label={
                              <>
                                {template.name}
                                <span className="text-gray-400">
                                  {" "}
                                  ({template.vertical} · {template.region})
                                </span>
                              </>
                            }
                          />
                          {merchant && (
                            <NativeSelect
                              aria-label={`Role on ${template.name} in workspace ${index + 1}`}
                              size="sm"
                              className="w-auto"
                              value={isAdmin ? "workspace_admin" : merchant.role}
                              disabled={isAdmin}
                              onChange={(event) =>
                                setMerchantRole(index, template.key, event.target.value as ScenarioMerchantRole)
                              }
                              options={
                                isAdmin
                                  ? [{ value: "workspace_admin", label: roleLabel("workspace_admin") }]
                                  : MERCHANT_ROLE_OPTIONS
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        <Section
          step={3}
          title="What data should be available?"
          sub="Everything is on. Turn something off and that page shows its empty state. Data that another line needs comes on with it."
          aside={
            <span className="flex shrink-0 items-center gap-2">
              <Button color="secondary" size="sm" onClick={() => setScenarios(seedOptions.map((o) => o.name))}>
                All on
              </Button>
              <Button color="secondary" size="sm" onClick={() => setScenarios([])}>
                All off
              </Button>
            </span>
          }
        >
          <div className="grid grid-cols-2 items-start gap-3">
            {groups.map(([area, items]) => {
              const on = items.filter((item) => scenarios.includes(item.name)).length;
              const setArea = (value: boolean) =>
                setScenarios(
                  items.reduce((next, item) => toggleScenario(seedOptions, next, item.name, value), scenarios),
                );
              return (
                <div key={area} className="flex flex-col gap-1 rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center gap-3 px-2">
                    <h4 className="text-sm font-semibold text-gray-900">{area}</h4>
                    <span className="text-xs text-gray-400 tabular-nums">
                      {on} of {items.length} on
                    </span>
                    <span className="ml-auto flex items-center gap-2">
                      {(
                        [
                          ["All on", true, on === items.length],
                          ["All off", false, on === 0],
                        ] as const
                      ).map(([label, value, disabled]) => (
                        <button
                          key={label}
                          type="button"
                          aria-label={`${value ? "Turn on" : "Turn off"} all ${area}`}
                          disabled={disabled}
                          onClick={() => setArea(value)}
                          className="cursor-pointer text-xs font-medium text-gray-500 transition-colors hover:text-gray-800 disabled:cursor-default disabled:text-gray-300"
                        >
                          {label}
                        </button>
                      ))}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {items.map((item) => (
                      <Toggle
                        key={item.name}
                        aria-label={item.label}
                        className="rounded-md px-2 py-1.5 hover:bg-gray-50"
                        isSelected={scenarios.includes(item.name)}
                        onChange={(value) => setScenarios(toggleScenario(seedOptions, scenarios, item.name, value))}
                        hint={
                          <span
                            className={scenarios.includes(item.name) ? "text-gray-800" : "text-gray-500 line-through"}
                          >
                            {item.label}
                          </span>
                        }
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        <section className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-baseline gap-3">
            <h3 className="text-md font-semibold text-gray-900">All scenarios</h3>
            <span className="text-sm text-gray-500 tabular-nums">{runs.length}</span>
          </div>
          {runs.length === 0 ? (
            <p className="text-sm text-gray-500">No scenarios yet. Create one above and it will be listed here.</p>
          ) : (
            <div className="flex flex-col divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
              {runs.map((run) => (
                <div key={run.id} className="flex items-center gap-4 px-4 py-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-semibold text-gray-900">{run.name}</span>
                    <span className="truncate text-xs text-gray-500">
                      {run.user.workspaces.map((w) => w.name).join(", ")} · {runRoleSummary(run)}
                    </span>
                  </div>
                  <span className="shrink-0 text-xs text-gray-400">{new Date(run.createdAt).toLocaleDateString()}</span>
                  <Button color="link-color" size="sm" onClick={() => onOpen(run.user)}>
                    Open
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {failure && <p className="text-sm text-error-primary">{failure.message}</p>}

      <div className="flex flex-wrap items-center gap-3 border-t border-gray-200 pt-5">
        <Button color="secondary" size="md" onClick={onBack}>
          Back
        </Button>
        <span className="ml-auto text-sm text-gray-500">
          {watchedWorkspaces.length} workspace{watchedWorkspaces.length === 1 ? "" : "s"} · {brandCount} merchant
          {brandCount === 1 ? "" : "s"} · {scenarios.length} of {seedOptions.length} data sets
        </span>
        <Button color="secondary" size="md" onClick={save} isDisabled={saveConfig.isPending}>
          Save scenario
        </Button>
        <Button color="primary" size="md" onClick={create} isDisabled={createScenario.isPending}>
          {createScenario.isPending ? "Creating…" : "Create scenario"}
        </Button>
      </div>
    </div>
  );
};
